import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import { AlertCircle } from 'lucide-react';
import Sidebar from './components/Sidebar';
import UserSettingsModal from './components/UserSettingsModal';
import ToastContainer from './components/ToastContainer';
import ErrorBoundary from './components/ErrorBoundary';
import SuspenseWithTimeout from './components/SuspenseWithTimeout';
import { LoadingMonitorBanner } from './components/LoadingMonitorBanner';
import { useAutomaticCalendarSync } from './hooks/useAutomaticCalendarSync';
import { useSessionHealthCheck } from './hooks/useSessionHealthCheck';
import { useNylasAuthStore } from './store/nylasAuth';

const LoginPage = React.lazy(() => import('./pages/Login'));
const DashboardPage = React.lazy(() => import('./pages/Dashboard'));
const AnalyticsDashboardPage = React.lazy(() => import('./pages/AnalyticsDashboard'));
const SearchPage = React.lazy(() => import('./pages/Search'));
const AdminPage = React.lazy(() => import('./pages/Admin'));
const EmployeeSearchPage = React.lazy(() => import('./pages/EmployeeSearch'));
const IntelligencePage = React.lazy(() => import('./pages/Intelligence'));
const StudyFinderPage = React.lazy(() => import('./pages/StudyFinder'));
const DealsPage = React.lazy(() => import('./pages/Deals'));
const DealDetailsPage = React.lazy(() => import('./pages/DealDetails'));
const ReportsPage = React.lazy(() => import('./pages/Reports'));
const SitesPage = React.lazy(() => import('./pages/Sites'));
const EmailGeneratorPage = React.lazy(() => import('./pages/EmailGenerator'));
const TasksPage = React.lazy(() => import('./pages/Tasks'));
const CompaniesPage = React.lazy(() => import('./pages/Companies'));
const CompanyProfilePage = React.lazy(() => import('./pages/CompanyProfile'));
const ContactsPage = React.lazy(() => import('./pages/Contacts'));
const SiteDetailsPage = React.lazy(() => import('./pages/SiteDetails'));
const ContactDetailsPage = React.lazy(() => import('./pages/ContactDetails'));
const CalendarPage = React.lazy(() => import('./pages/Calendar'));
const EmailPage = React.lazy(() => import('./pages/Email'));
const OutlookCallbackPage = React.lazy(() => import('./pages/OutlookCallback'));
const FilesPage = React.lazy(() => import('./pages/Files'));
const PrincipalInvestigatorsPage = React.lazy(() => import('./pages/PrincipalInvestigators'));
const WeeklyActivityPage = React.lazy(() => import('./pages/WeeklyActivity'));

// Retry configuration
const RETRY_DELAYS = [1000, 2000, 4000]; // Delays in milliseconds
const MAX_RETRIES = RETRY_DELAYS.length;

async function retryOperation(operation: () => Promise<any>, attempt = 0): Promise<any> {
  try {
    return await operation();
  } catch (error: any) {
    if (attempt >= MAX_RETRIES || !error.message?.includes('Failed to fetch')) {
      throw error;
    }
    
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]));
    return retryOperation(operation, attempt + 1);
  }
}

function AppContent() {
  const { user, sessionValid } = useAuthStore();
  const [showUserSettings, setShowUserSettings] = useState(false);
  const { initialize: initializeNylas, reset: resetNylas } = useNylasAuthStore();

  useEffect(() => {
    if (user) {
      initializeNylas();
    } else {
      resetNylas();
    }
  }, [user?.id]);

  // Enable automatic calendar sync
  useAutomaticCalendarSync();

  // Enable session health checks when authenticated
  useSessionHealthCheck();

  return (
    <>
      <ToastContainer />
      <LoadingMonitorBanner />
      <div className="min-h-screen bg-surface-background">

        {user ? (
          <div className="flex h-screen overflow-hidden">
            <Sidebar onOpenSettings={() => setShowUserSettings(true)} />

            <main className="flex-1 overflow-y-auto">
              <ErrorBoundary>
                <SuspenseWithTimeout
                  fallback={
                    <div className="flex items-center justify-center min-h-screen">
                      <div className="relative">
                        <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200 border-t-sky-500"></div>
                        <div className="absolute inset-0 rounded-full bg-sky-400/20 blur-xl animate-pulse-slow"></div>
                      </div>
                    </div>
                  }
                >
                  <Routes>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/analytics" element={<AnalyticsDashboardPage />} />
                    <Route path="/search" element={<SearchPage />} />
                    <Route path="/employees" element={<EmployeeSearchPage />} />
                    <Route path="/companies" element={<CompaniesPage />} />
                    <Route path="/companies/:id" element={<CompanyProfilePage />} />
                    <Route path="/contacts" element={<ContactsPage />} />
                    <Route path="/contacts/:id" element={<ContactDetailsPage />} />
                    <Route path="/files" element={<FilesPage />} />
                    <Route path="/files/:folderId" element={<FilesPage />} />
                    <Route path="/intelligence" element={<IntelligencePage />} />
                    <Route path="/studies" element={<StudyFinderPage />} />
                    <Route path="/deals" element={<DealsPage />} />
                    <Route path="/deals/:id" element={<DealDetailsPage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/sites" element={<SitesPage />} />
                    <Route path="/sites/:id" element={<SiteDetailsPage />} />
                    <Route path="/principal-investigators" element={<PrincipalInvestigatorsPage />} />
                    <Route path="/calendar" element={<CalendarPage />} />
                    <Route path="/email" element={<EmailPage />} />
                    <Route path="/email-generator" element={<EmailGeneratorPage />} />
                    <Route path="/tasks" element={<TasksPage />} />
                    <Route path="/outlook-callback" element={<OutlookCallbackPage />} />
                    {user.role === 'admin' && (
                      <Route path="/admin" element={<AdminPage />} />
                    )}
                    {user.role === 'admin' && (
                      <Route path="/weekly-activity" element={<WeeklyActivityPage />} />
                    )}
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </SuspenseWithTimeout>
              </ErrorBoundary>
            </main>

            {showUserSettings && (
              <UserSettingsModal onClose={() => setShowUserSettings(false)} />
            )}
          </div>
        ) : (
          <ErrorBoundary>
            <SuspenseWithTimeout
              fallback={
                <div className="flex items-center justify-center min-h-screen">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200 border-t-sky-500"></div>
                    <div className="absolute inset-0 rounded-full bg-sky-400/20 blur-xl animate-pulse-slow"></div>
                  </div>
                </div>
              }
            >
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/outlook-callback" element={<OutlookCallbackPage />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </SuspenseWithTimeout>
          </ErrorBoundary>
        )}
      </div>
    </>
  );
}

function App() {
  const { initialize, loading, user, sessionValid } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        await initialize();
      } catch (err: any) {
        console.error('Failed to initialize auth:', err);
        if (err.message?.includes('refresh_token_not_found')) {
          localStorage.clear();
          sessionStorage.clear();
          setError('Your session has expired. Please sign in again.');
        } else {
          setError('Failed to connect to authentication service');
        }
      }
    };
    init();
  }, [initialize]);

  // Only redirect to login if session is completely unrecoverable
  // Allow time for background refresh to work before redirecting
  useEffect(() => {
    if (!loading && !sessionValid && user) {
      // Give the session refresh mechanism time to work (15 seconds)
      // This prevents premature redirects while silent refresh is attempting
      const timeoutId = setTimeout(() => {
        // Double-check session is still invalid after delay
        if (!sessionValid) {
          setError('Your session has expired. Please sign in again.');
          setTimeout(() => {
            window.location.href = '/login';
          }, 3000);
        }
      }, 15000);

      return () => clearTimeout(timeoutId);
    }
  }, [sessionValid, loading, user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <Router>
      {error && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-2xl w-full mx-4">
          <div className="bg-red-50 border-l-4 border-red-500 rounded-lg shadow-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="ml-3 text-sm font-medium text-red-800">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-4 text-red-500 hover:text-red-700 transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
      <AppContent />
    </Router>
  );
}

export default App;